/**
 * The KB article's governed acts, on the record (#1744 slice 7).
 *
 * ── nothing here decides authority ──────────────────────────────────────────
 * The issue rules that authority must *"never be inferred from a persona"*, and
 * this component cannot infer it even if it wanted to: it renders the finite
 * `actions` list that `service.my_kb_article_context` returns and nothing else.
 * There is no status comparison, no role name, no persona and no capability
 * guess in this file. If the server does not name an act, the button is not in
 * the accessibility tree — the same affordance-is-a-prop rule `ProcessBar` and
 * `RecordShell` already state about themselves.
 *
 * That is also why the context door exists at all: `core.can_act` is never
 * called from TypeScript anywhere in the fleet, and there is no
 * `core.my_capabilities`. A surface can either ask the server or guess.
 *
 * ── why a per-entity mount and not a registrar ──────────────────────────────
 * There is no record-action registrar in the platform — no
 * `core.register_record_action`, no `core.action` table. Inventing one is a
 * platform-shape decision that belongs in Forge, not in this file. So this
 * follows the console's OWN existing idiom for a record-specific surface,
 * `ShellDrawer`'s `RECORD_INSIGHT`, whose comment states the pattern: *"A module
 * registers what 'insight' means for its own record here, so the drawer
 * dispatches instead of accumulating `if (entity …)`."*
 *
 * ── the rail is the DECLARED machine, not a list in this file ───────────────
 * Stages come from `core.status_machine` through `useEntityStatusMachine`, so
 * the three words on screen are the three words slice 2 declared. A label
 * edited in the machine moves this rail with no code change.
 */
import { useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, ProcessBar, type ProcessStage } from '@solum/ui'
import { useSolum } from '../data/client'
import { useEntityStatusMachine } from '../data/queries'
import { QueryBoundary, queryState } from '../data/QueryBoundary'
import s from './KbArticleProcess.module.css'
import { readableError } from '@solum/sdk'

/** Exactly what `service.my_kb_article_context` returns. */
interface KbContext {
  status: string
  canAct: boolean
  actions: string[]
  blocker: string | null
  blockers: { code: string; detail: string }[]
  reachesCustomers: boolean
  publishedAt: string | null
}

/** The server's blocker codes, said in words. Presentation only — the server
 *  decides, this only spells. An unknown code prints itself rather than
 *  vanishing, because a silent unknown is how a new refusal goes unnoticed. */
const BLOCKER_TEXT: Record<string, string> = {
  no_publication_authority: 'Releasing an article takes publication authority. Yours lets you write it, not publish it.',
  no_authority: 'You can read this article but not change it.',
  already_retired: 'This article is retired.',
  figures_not_verified: 'A figure this article references has not been verified by the server yet.',
  nothing_to_do: 'There is nothing to do here right now.',
}

/** How each act is SPELLED. Presentation only: the server decides which acts
 *  exist, this decides nothing. An act with no entry here still renders — under
 *  its own code — because a new governed verb appearing as an unlabelled button
 *  is a bug someone fixes, and one that silently does not appear is a bug
 *  nobody sees. Same argument as BLOCKER_TEXT above. */
const ACT_LABEL: Record<string, string> = {
  publish: 'Publish',
  submit: 'Submit for review',
  withdraw: 'Withdraw',
  retract: 'Retract',
  retire: 'Retire',
}

export function KbArticleProcess({ id }: { id: string }): ReactNode {
  const { client } = useSolum()
  const raw = client.raw()
  const qc = useQueryClient()
  const [pending, setPending] = useState<string | null>(null)

  const machine = useEntityStatusMachine('service_kb_article')
  const ctx = useQuery({
    queryKey: ['kb-article-context', id],
    enabled: !!id && id !== 'new',
    queryFn: () => raw.rpcScalar<KbContext | null>('my_kb_article_context', { p_id: id }, 'service'),
  })

  const act = useMutation({
    mutationFn: (action: string) =>
      raw.rpcScalar<null>(`${action}_kb_article`, { p_id: id }, 'service'),
    onSuccess: () => {
      setPending(null)
      void qc.invalidateQueries({ queryKey: ['kb-article-context', id] })
      void qc.invalidateQueries({ queryKey: ['entity-row', 'service_kb_article', id] })
    },
  })

  // The read is gated the way every other console read is; a refused or broken
  // context must not render as "no acts available", which is the one wrong
  // answer this surface can give.
  const gate = queryState([ctx], [machine])
  if (gate.kind !== 'ready') {
    return (
      <QueryBoundary queries={[ctx]} optional={[machine]} label="this article’s acts">
        {() => null}
      </QueryBoundary>
    )
  }
  // An article the caller may see but the door answered null for: nothing to
  // offer and nothing to explain. Silence, matching the door.
  const c = ctx.data
  if (!c) return null

  return (
    <KbProcessView
      context={c}
      states={(machine.data?.states ?? []).map((st) => ({ code: st.code, label: st.label }))}
      confirming={pending === 'publish'}
      busy={act.isPending}
      pending={pending}
      failure={act.isError
        ? (readableError(act.error, 'The act could not be recorded.'))
        : undefined}
      onAct={(a) => {
        // Publishing something customer-facing asks once. The SERVER is what
        // says it reaches customers; this only relays that.
        if (a === 'publish' && c.reachesCustomers && pending !== 'publish') { setPending('publish'); return }
        setPending(a); act.reset(); act.mutate(a)
      }}
    />
  )
}

/**
 * The view, split out and exported so the property that matters is testable
 * without a client: **an act the server did not name is not in the DOM at all**
 * — not a disabled button, not a tooltip. Same rule `ProcessBar` states about
 * its own `advance` prop.
 */
export function KbProcessView({
  context: c, states, confirming, busy, pending, failure, onAct,
}: {
  context: KbContext
  states: { code: string; label: string }[]
  confirming: boolean
  busy: boolean
  pending: string | null
  failure?: string
  onAct: (action: string) => void
}): ReactNode {
  const stages: ProcessStage[] = states.map((st) => ({
    key: st.code,
    label: st.label,
    state: st.code === c.status ? 'active' : 'upcoming',
  }))
  const activeIdx = stages.findIndex((st) => st.state === 'active')
  for (let i = 0; i < activeIdx; i += 1) stages[i]!.state = 'done'

  const offered = (a: string) => c.actions.includes(a)

  /* Every act EXCEPT the one ProcessBar's `advance` already renders. Derived
     rather than listed: this used to be the tuple `['retract', 'retire']`, and
     #1744's review handover added two more verbs the moment the lifecycle grew
     a state — a hand-typed list here is a SELECTOR, and a selector narrower
     than the property is indistinguishable from working (rule 32). The server
     names the acts; this only decides where each one is drawn. */
  const secondary = c.actions.filter((a) => a !== 'publish')

  const note: ReactNode = offered('publish')
    ? (c.reachesCustomers
      ? 'This article is customer-facing — publishing releases it to customers.'
      : 'This article is internal — publishing releases it to colleagues only.')
    : c.blocker
      ? BLOCKER_TEXT[c.blocker] ?? c.blocker
      : undefined

  return (
    <div className={s.wrap} data-testid="kb-process">
      <ProcessBar
        stages={stages}
        label={`Knowledge article — ${c.status}`}
        positionText={c.status}
        testId="kb-process-rail"
        advance={offered('publish')
          ? {
            label: confirming ? 'Confirm publish' : 'Publish',
            note,
            onAdvance: () => onAct('publish'),
            loading: busy && pending === 'publish',
            error: failure,
            testId: 'kb-publish',
            noteTestId: 'kb-publish-note',
            errorTestId: 'kb-act-error',
          }
          : undefined}
      />

      {/* The acts ProcessBar has no slot for. Rendered only where the server
          named them — a withheld act is absent, never disabled-and-teasing. */}
      {secondary.length > 0 && (
        <div className={s.acts} data-testid="kb-acts">
          {secondary.map((a) => (
            <Button
              key={a}
              variant="ghost"
              loading={busy && pending === a}
              onClick={() => onAct(a)}
              data-testid={`kb-${a}`}
            >{ACT_LABEL[a] ?? a}</Button>
          ))}
        </div>
      )}

      {/* The reason there is nothing to do, when there is nothing to do.
          Caught by its own test: this line used to live ONLY on ProcessBar's
          `advance`, which renders only when publish is offered — so in the one
          case that matters most, a reader who may do nothing, the surface
          explained nothing. */}
      {!offered('publish') && note && (
        <p className={s.note} data-testid="kb-blocker-note">{note}</p>
      )}

      {/* Why it is not ready — the SERVER's own sentences, not a re-derivation. */}
      {c.blockers.length > 0 && (
        <ul className={s.blockers} data-testid="kb-blockers">
          {c.blockers.map((b) => (
            <li key={`${b.code}:${b.detail}`} data-testid="kb-blocker">{b.detail}</li>
          ))}
        </ul>
      )}

      {/* A refusal that arrived while no advance button is showing still has to
          be visible — ProcessBar only renders `error` beside an advance. */}
      {failure && !offered('publish') && (
        <p role="alert" className={s.error} data-testid="kb-act-error">{failure}</p>
      )}
    </div>
  )
}
